import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CartDrawer } from '@/components/shop/CartDrawer';
import logoFull from '@/assets/sampaoli-logo-full.png.asset.json';
export function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    if (!isHomePage) return;
    const handleScroll = () => {
      setScrolledPastHero(window.scrollY > window.innerHeight * 0.6);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  // On homepage before scroll: light text on dark hero
  // On homepage after scroll: dark text on light background
  // On other pages: always light text on dark header
  const useDarkText = isHomePage && scrolledPastHero;

  const navItems = [
    { href: '/', label: t('nav.home') },
    { href: '/collections', label: t('nav.collections') },
    { href: '/shop', label: t('nav.shop') },
    { href: '/about', label: t('nav.about') },
    { href: '/blog', label: 'Blog' },
  ];

  const isActive = (path: string) => location.pathname === path;

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  

  return (
    <header className={cn(
      "fixed left-0 right-0 z-50 transition-all duration-300",
      isHomePage
        ? scrolledPastHero
          ? "top-8 bg-cream/90 backdrop-blur-sm shadow-sm"
          : "top-8 bg-transparent"
        : "top-0 bg-charcoal/95 backdrop-blur-sm border-b border-bronze/10"
    )}>
      <div className="container-wide">
        <div className="flex items-center justify-between h-[84px] md:h-[132px]">
          {/* Logo */}
          <Link to="/" aria-label="Sampaoli Italian Creations – home">
            <span className="inline-flex items-center rounded-lg bg-cream px-3 py-2 shadow-md ring-1 ring-charcoal/10">
              <img
                src={logoFull.url}
                alt="Sampaoli Italian Creations – Passion for details"
                className="h-16 w-auto object-contain md:h-[96px]"
                width={1504}
                height={1500}
              />
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'link-underline font-body text-sm tracking-wide transition-colors',
                  isHomePage && !scrolledPastHero && 'drop-shadow-sm',
                  isActive(item.href)
                    ? useDarkText ? 'text-charcoal font-medium' : isHomePage ? 'text-cream font-medium' : 'text-bronze font-medium'
                    : useDarkText ? 'text-charcoal/70 hover:text-charcoal' : isHomePage ? 'text-ivory/80 hover:text-ivory' : 'text-cream/80 hover:text-cream'
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Switcher */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => changeLanguage(i18n.language === 'it' ? 'en' : 'it')}
              className={cn(
                "font-body text-xs font-semibold tracking-widest uppercase px-2 transition-colors",
                useDarkText
                  ? "text-charcoal hover:text-charcoal hover:bg-charcoal/10"
                  : "text-cream hover:text-cream hover:bg-white/10"
              )}
              aria-label="Switch language"
            >
              {i18n.language === 'it' ? 'IT' : 'EN'}
            </Button>

            {/* Cart Drawer */}
            <CartDrawer triggerClassName={cn(
              "relative",
              useDarkText
                ? "text-charcoal hover:text-charcoal/70 hover:bg-charcoal/10"
                : "text-cream hover:text-bronze hover:bg-white/10"
            )} />

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "md:hidden transition-colors",
                useDarkText
                  ? "text-charcoal hover:text-charcoal hover:bg-charcoal/10"
                  : "text-cream hover:text-cream hover:bg-white/10"
              )}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 bg-charcoal/90 backdrop-blur-sm animate-fade-in">
            <div className="flex flex-col gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    'px-4 py-3 rounded-lg font-body text-base transition-colors',
                    isActive(item.href)
                      ? 'bg-white/10 text-cream font-medium'
                      : 'text-ivory/80 hover:bg-white/10 hover:text-ivory'
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
